/**
 * GameAdapter over the real game objects. Hooks are installed by patching
 * the *instances* (chunks.setBlock / batchEdits, entities.tick / attack), so
 * the shared engine files stay untouched apart from EntityManager.syncViews().
 *
 *  - Block edits: every top-level ChunkManager.setBlock is reported to the
 *    session; ones inside batchEdits() (water/fire/TNT sims) are flagged as
 *    simulation (clients keep those local, the host's sim is authoritative).
 *  - Remote edits to chunks that aren't generated yet are recorded in the
 *    WorldSave (so the gen worker bakes them in) and re-applied once the
 *    chunk is ready.
 *  - Mobs: on clients EntityManager.tick() becomes a replica tick that sets
 *    positions from interpolated network snapshots; hits are forwarded to the
 *    host. On the host the mobs see the remote players as targets, so they
 *    chase and hurt everyone in the room (net/objects.ts ships the hits).
 *  - Dropped items, XP orbs, arrows, chest/furnace contents, client-lit TNT
 *    and damage to remote players live in net/objects.ts (GameObjectNet);
 *    this file only supplies the adapters and the 20 Hz tick.
 */

import type { ChunkManager } from '../world/manager';
import type { WorldSave } from '../world/save';
import type { Player } from '../player/player';
import type { EntityManager } from '../entities/manager';
import type { Mob } from '../entities/mob';
import { deserializeExtras, isEmpty, serializeExtras, setStack, type DamageSource, type MobType, type Stack } from '../game/types';
import { ChunkState } from '../world/chunk';
import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { WORLDGEN_VERSION } from '../worldgen/terrain';
import { liveItemManagers, type ItemEntityManager } from '../entities/itemEntities';
import type { EntityHost, PlayerTarget } from '../entities/host';
import { liveFurnaces, liveInventory, liveTnt, liveXp } from './handles';
import { GameObjectNet, type NetHit } from './objects';
import {
  AF_PLAYER, AF_STUCK, BE_CHEST, DK_ITEM, EF_AGGRO, EF_DYING, EF_FIRE, EF_SHEARED, MOB_TYPES, PF_BOW, PF_DEAD, PF_FIRE, PF_FLY, PF_SNEAK, PF_SPRINT, PF_SWIM,
  stackWith, type ArrowState, type EntitiesMsg, type EntityState, type PlayerState,
} from './codec';
import { INTERP_DELAY_MS, SnapshotBuffer } from './interp';
import type { GameAdapter, MobUseFx, NetSession, RemotePlayerView } from './session';

const CHUNK_AREA = CHUNK_SIZE * CHUNK_SIZE;
/** x y z yaw headYaw pitch limbSwing limbAmt swell attackAnim */
const MOB_W = 10;
const MOB_ANGLES = [3, 4];
/** Chest contents travel as [id, count, dmg] per slot, then the enchantment / name tail (game/types serializeExtras). */
const CHEST_SLOTS = 27;

export interface GameDeps {
  seed: number;
  chunks: ChunkManager;
  save: WorldSave;
  player: Player;
  entities: EntityManager;
  /** main.ts's entity host (its `player` is the local one: shields, armor, stats). */
  entityHost: EntityHost;
  sky: { time: number };
  health: () => number;
  dead: () => boolean;
  /** true when the client had a saved position for this room before joining. */
  hadSavedPosition: boolean;
  /** Host: a player right-clicked `mob` holding `held` (main.ts runs the same
   *  code its own right-click does); null when the item does nothing there. */
  useOnMob?: (mob: Mob, held: Stack) => MobUseFx | null;
}

interface Replica { mob: Mob; buf: SnapshotBuffer; last: EntityState }

export class GameBridge implements GameAdapter {
  readonly seed: number;
  readonly worldgenVersion = WORLDGEN_VERSION;
  private _session: NetSession | null = null;
  /** Live session (net/index.ts assigns it; the object net rides along). */
  get session(): NetSession | null { return this._session; }
  set session(s: NetSession | null) {
    this._session = s;
    this.objs.attach(s);
  }
  private replica = false;
  private suppress = 0;
  private depth = 0;
  private simDepth = 0;
  private readonly deferred = new Map<string, Array<[number, number, number, number]>>();
  private readonly replicas = new Map<number, Replica>();
  private readonly netIdOf = new WeakMap<Mob, number>();
  private readonly origSetBlock: ChunkManager['setBlock'];
  private readonly origTick: EntityManager['tick'];
  private readonly origAttack: EntityManager['attack'];
  private readonly origAttackPlayer: EntityManager['attackPlayer'];
  private readonly tmp = new Float64Array(MOB_W);
  /** Gameplay objects: drops, orbs, arrows, block entities, TNT, damage. */
  readonly objs: GameObjectNet;
  /** Remote players the mobs may target (rebuilt every tick while hosting). */
  private readonly remoteTargets = new Map<string, RemotePlayer>();
  private readonly outTargets: RemotePlayer[] = [];
  /** Scratch for NetSession.remotePlayers() (the client's own hit targets). */
  private readonly peerViews: RemotePlayerView[] = [];
  /** Chests within reach, cached so a keyframe does not walk the whole map. */
  private readonly chestIndex = new Map<string, { x: number; y: number; z: number }>();

  constructor(private readonly d: GameDeps) {
    this.seed = d.seed;
    const chunks = d.chunks;
    this.origSetBlock = chunks.setBlock.bind(chunks);
    chunks.setBlock = (...args: Parameters<ChunkManager['setBlock']>) => {
      if (this.suppress > 0 || !this.session || this.depth > 0) return this.origSetBlock(...args);
      const [x, y, z, id] = args;
      const prev = chunks.world.getBlock(x, y, z);
      this.depth++;
      let ok = false;
      try { ok = this.origSetBlock(...args); } finally { this.depth--; }
      if (ok) this.session.localEdit(x, y, z, id, prev, this.simDepth === 0);
      return ok;
    };
    const origBatch = chunks.batchEdits.bind(chunks);
    chunks.batchEdits = (...args: Parameters<ChunkManager['batchEdits']>) => {
      this.simDepth++;
      try { origBatch(...args); } finally { this.simDepth--; }
    };
    const ents = d.entities;
    this.origTick = ents.tick.bind(ents);
    ents.tick = (...args: Parameters<EntityManager['tick']>) => { if (this.replica) this.replicaTick(); else this.origTick(...args); };
    this.origAttack = ents.attack.bind(ents);
    ents.attack = (...args: Parameters<EntityManager['attack']>) => {
      // forward every argument (enchant options etc.) untouched to the real attack
      if (!this.replica || !this.session) return this.origAttack(...args);
      const [mob, dmg, fx, fz, kb] = args;
      const id = this.netIdOf.get(mob);
      if (id === undefined || !mob.alive) return false;
      this.session.hitMob(id, dmg, fx, fz, kb ?? 0.4);
      return true;
    };
    this.origAttackPlayer = ents.attackPlayer.bind(ents);
    ents.attackPlayer = (...args: Parameters<EntityManager['attackPlayer']>) => {
      // a client does not own anyone else's health: the hit goes to the host,
      // which applies it and tells the victim (same split as mob damage)
      if (!this.replica || !this.session) return this.origAttackPlayer(...args);
      const [target, dmg, fx, fz, kb] = args;
      if (!target.id || !target.alive) return false;
      this.session.hitPlayer(target.id, dmg, fx, fz, kb ?? 0.4);
      return true;
    };
    this.objs = new GameObjectNet({
      session: () => this.session,
      isHost: () => this.session?.isHost ?? false,
      getBlock: (x, y, z) => this.getBlock(x, y, z),
      items: () => this.items(),
      xp: () => liveXp(),
      arrows: () => this.d.entities.arrows,
      spawnDrop: (drop, netId, origin, vx, vy, vz) => this.spawnDrop(drop, netId, origin, vx, vy, vz),
      removeDrop: (kind, netId) => this.removeDrop(kind, netId),
      spawnArrow: (a) => this.spawnArrow(a),
      removeArrow: (localId) => {
        const a = this.d.entities.arrows.find((x) => x.id === localId);
        if (a) a.removed = true;
      },
      beNear: (kind, x, y, z, r) => this.beNear(kind, x, y, z, r),
      beWrite: (kind, x, y, z, flat) => this.beWrite(kind, x, y, z, flat),
      beDrop: (kind, x, y, z) => this.beDrop(kind, x, y, z),
      primeTnt: (x, y, z) => liveTnt()?.prime(x, y, z),
      localHit: (hit) => this.applyRemoteHit(hit),
      giveItem: (item, count, dmg, ex) => this.giveItem(item, count, dmg, ex),
      me: () => {
        const p = this.d.player;
        return { x: p.position.x, y: p.position.y, z: p.position.z };
      },
      players: () => this.players(),
    });
  }

  /** Detach the hooks (leaving multiplayer). */
  dispose(): void {
    this._session = null;
    this.objs.detach();
    this.setReplica(false);
  }

  // ---------------------------------------------------------------- blocks

  getBlock(x: number, y: number, z: number): number {
    if (y < 0 || y >= WORLD_HEIGHT) return 0;
    const c = this.d.chunks.world.getChunk(x >> 4, z >> 4);
    if (!c || c.state < ChunkState.Generated) return -1;
    return this.d.chunks.world.getBlock(x, y, z);
  }

  setBlockRemote(x: number, y: number, z: number, id: number): void {
    if (y < 0 || y >= WORLD_HEIGHT) return;
    const prev = this.getBlock(x, y, z);
    // a client turning TNT into air lit it: the host primes the charge too
    if (prev >= 0) this.objs.remoteBlockEdit(prev, id, x, y, z);
    const cx = x >> 4, cz = z >> 4;
    const c = this.d.chunks.world.getChunk(cx, cz);
    if (c && c.state >= ChunkState.Generated) {
      this.suppress++;
      try { this.origSetBlock(x, y, z, id); } finally { this.suppress--; }
      return;
    }
    // not generated yet: bake into the save (gen applies it) and re-apply when ready
    this.d.save.record(cx, cz, (x & 15) + (z & 15) * CHUNK_SIZE + y * CHUNK_AREA, id);
    const k = cx + ',' + cz;
    let list = this.deferred.get(k);
    if (!list) { list = []; this.deferred.set(k, list); }
    list.push([x, y, z, id]);
  }

  /** Re-apply edits whose chunk was still generating when they arrived. */
  flushDeferred(): void {
    if (this.deferred.size === 0) return;
    const w = this.d.chunks.world;
    for (const [k, list] of this.deferred) {
      const [cx, cz] = k.split(',').map(Number);
      const c = w.getChunk(cx, cz);
      if (!c || c.state < ChunkState.Generated) continue;
      this.deferred.delete(k);
      this.d.chunks.batchEdits(() => {
        this.suppress++;
        try { for (const [x, y, z, id] of list) this.origSetBlock(x, y, z, id); } finally { this.suppress--; }
      });
    }
  }

  exportDelta(): Int32Array {
    // WorldSave keeps its per-chunk edit maps private; read them structurally
    // (net-only coupling, documented in docs/deploy.md TODOs).
    const s = this.d.save as unknown as { edits?: Map<number, Map<number, number>>; coords?: Map<number, string> };
    if (!(s.edits instanceof Map) || !(s.coords instanceof Map)) {
      console.warn('[net] WorldSave layout changed; snapshot carries no base edits');
      return new Int32Array(0);
    }
    const out: number[] = [];
    for (const [key, m] of s.edits) {
      const coord = s.coords.get(key);
      if (!coord) continue;
      const [cx, cz] = coord.split(',').map(Number);
      for (const [idx, id] of m) out.push(cx, cz, idx, id);
    }
    return new Int32Array(out);
  }

  importDelta(q: Int32Array): void {
    this.d.chunks.batchEdits(() => {
      for (let i = 0; i + 3 < q.length; i += 4) {
        const cx = q[i], cz = q[i + 1], idx = q[i + 2], id = q[i + 3];
        if (idx < 0 || idx >= CHUNK_AREA * WORLD_HEIGHT) continue;
        const x = cx * CHUNK_SIZE + (idx & 15), z = cz * CHUNK_SIZE + ((idx >> 4) & 15), y = Math.floor(idx / CHUNK_AREA);
        this.setBlockRemote(x, y, z, id);
      }
    });
  }

  // ---------------------------------------------------------------- player

  localState(tick: number): PlayerState {
    const p = this.d.player;
    const v = p.poseView(1);
    let flags = 0;
    if (v.sneaking) flags |= PF_SNEAK;
    if (v.sprinting) flags |= PF_SPRINT;
    if (v.swimming) flags |= PF_SWIM;
    if (v.flying) flags |= PF_FLY;
    if (v.usingBow) flags |= PF_BOW;
    if (v.onFire) flags |= PF_FIRE;
    if (this.d.dead()) flags |= PF_DEAD;
    return {
      tick, x: v.x, y: v.y, z: v.z, yaw: v.yaw, headYaw: v.headYaw, pitch: v.pitch,
      limbSwing: v.limbSwing, limbSwingAmount: v.limbSwingAmount, swing: v.swing, flags,
      hurtTime: v.hurtTime, heldItem: v.heldItem, armor: [...v.armor] as [number, number, number, number],
      health: this.d.health(),
    };
  }

  teleport(x: number, y: number, z: number): void { this.d.player.teleportTo(x, y, z); }
  hasSavedPosition(): boolean { return this.d.hadSavedPosition; }
  holding(): boolean { return this.d.player.isFrozen; }
  time(): number { return this.d.sky.time; }
  setTime(t: number): void { if (Number.isFinite(t)) this.d.sky.time = t; }

  // ---------------------------------------------------- world server mode

  /** The dimension the local player is in, as the world server numbers them. */
  dim(): number { return this.d.chunks.dimension === 'nether' ? 1 : 0; }

  /**
   * An authoritative block value from the world server. In the dimension we
   * are in it is a normal remote edit; in the other one it goes straight into
   * the save, so the chunks have it when we walk through a portal.
   */
  setBlockIn(dim: number, x: number, y: number, z: number, id: number): void {
    if (y < 0 || y >= WORLD_HEIGHT) return;
    if (dim === this.dim()) {
      if (this.getBlock(x, y, z) !== id) this.setBlockRemote(x, y, z, id);
      return;
    }
    const save = this.d.save;
    const here = save.dimension;
    save.dimension = dim === 1 ? 'nether' : 'overworld';
    try { save.record(x >> 4, z >> 4, (x & 15) + (z & 15) * CHUNK_SIZE + y * CHUNK_AREA, id); }
    finally { save.dimension = here; }
  }

  /** Persistent world flags (structure population markers): the sim role reports them. */
  worldFlags(): string[] { return [...this.d.save.flags]; }
  addWorldFlag(f: string): void { if (f.length > 0 && f.length <= 64) this.d.save.setFlag(f); }

  // ------------------------------------------------------------------ mobs

  mobStates(): EntityState[] {
    const out: EntityState[] = [];
    for (const m of this.d.entities.mobs) {
      const type = MOB_TYPES.indexOf(m.type as (typeof MOB_TYPES)[number]);
      if (type < 0 || m.removed) continue;
      const v = m.view;
      out.push({
        id: m.id, type,
        flags: (v.onFire ? EF_FIRE : 0) | (v.aggressive ? EF_AGGRO : 0) | (v.sheared ? EF_SHEARED : 0) | (m.dying ? EF_DYING : 0),
        x: m.x, y: m.y, z: m.z, yaw: m.yaw, headYaw: m.headYaw, pitch: m.pitch,
        limbSwing: m.limbSwing, limbSwingAmount: m.limbSwingAmount, hurtTime: m.hurtTime, deathTime: m.deathTime,
        swell: v.swell, attackAnim: v.attackAnim, color: m.color, heldItem: m.heldItem,
        name: m.customName || undefined,
      });
    }
    return out;
  }

  mobPos(id: number): [number, number, number] | null {
    const m = this.d.entities.mobs.find((x) => x.id === id && !x.removed);
    return m ? [m.x, m.y + m.height / 2, m.z] : null;
  }

  hitMob(id: number, dmg: number, fx: number, fz: number, kb: number): boolean {
    const m = this.d.entities.mobs.find((x) => x.id === id);
    return m ? this.origAttack(m, dmg, fx, fz, kb) : false;
  }

  /** Host: a client right-clicked mob `id` holding `held`. */
  useMob(id: number, held: Stack): MobUseFx | null {
    const m = this.d.entities.mobs.find((x) => x.id === id && !x.removed && x.alive);
    return m ? this.d.useOnMob?.(m, held) ?? null : null;
  }

  /** Client: the host's id for a mob we only mirror (null: it is ours to simulate). */
  remoteMobId(mob: Mob): number | null {
    return this.replica ? this.netIdOf.get(mob) ?? null : null;
  }

  /**
   * Host: where a player is, by peer id. The host's own body and the remote
   * targets it simulates both answer; anyone else is not in the room.
   */
  playerPos(id: string): [number, number, number] | null {
    if (id === this.session?.me.id) {
      const p = this.d.player.position;
      return [p.x, p.y + 0.9, p.z];
    }
    const t = this.remoteTargets.get(id);
    return t ? [t.x, t.y + t.height / 2, t.z] : null;
  }

  /** Host: a client's swing at a player landed. */
  hitPlayer(id: string, dmg: number, fx: number, fz: number, kb: number): boolean {
    const me = this.session?.me.id ?? '';
    if (id === me) {
      // it was aimed at us: our own stats take it, and the shield still works
      this.applyRemoteHit({
        amount: dmg, kind: 'player', x: fx, y: this.d.player.position.y, z: fz, attacker: 0,
        kbx: this.d.player.position.x - fx, kbz: this.d.player.position.z - fz, fire: false,
      });
      return true;
    }
    const t = this.remoteTargets.get(id);
    return t ? this.d.entities.attackPlayer(t, dmg, fx, fz, kb) : false;
  }

  setReplica(on: boolean): void {
    if (on === this.replica) return;
    this.replica = on;
    this.replicas.clear();
    this.objs.setReplica(on);
    this.d.entities.remotePlayers = [];
    if (on) this.d.entities.clear(); // the host's mobs arrive in the next keyframe
    // becoming host: the replicated Mob objects stay and start simulating
  }

  applyEntities(msg: EntitiesMsg, now: number): void {
    if (!this.replica) return;
    const ents = this.d.entities;
    const seen = msg.keyframe ? new Set<number>() : null;
    for (const e of msg.ents) {
      seen?.add(e.id);
      let r = this.replicas.get(e.id);
      if (!r) {
        const mob = ents.spawnMob(MOB_TYPES[e.type] as MobType, e.x, e.y, e.z);
        if (!mob) continue;
        mob.px = e.x; mob.py = e.y; mob.pz = e.z;
        r = { mob, buf: new SnapshotBuffer(MOB_W, MOB_ANGLES, 16), last: e };
        this.replicas.set(e.id, r);
        this.netIdOf.set(mob, e.id);
      }
      r.last = e;
      // every state an ent arrives in carries its name (absent = unnamed)
      r.mob.customName = e.name ?? '';
      if (e.name) r.mob.persistent = true;   // survives us taking over as host
      r.buf.push(now, [e.x, e.y, e.z, e.yaw, e.headYaw, e.pitch, e.limbSwing, e.limbSwingAmount, e.swell, e.attackAnim]);
    }
    const drop = (id: number): void => {
      const r = this.replicas.get(id);
      if (!r) return;
      r.mob.removed = true;
      this.replicas.delete(id);
    };
    for (const id of msg.removed) drop(id);
    if (seen) for (const id of [...this.replicas.keys()]) if (!seen.has(id)) drop(id);
  }

  private replicaTick(): void {
    this.flushDeferred();
    const rt = performance.now() - INTERP_DELAY_MS;
    const v = this.tmp;
    for (const r of this.replicas.values()) {
      const m = r.mob;
      if (!r.buf.sample(rt, v)) continue;
      m.px = m.x; m.py = m.y; m.pz = m.z;
      m.x = v[0]; m.y = v[1]; m.z = v[2];
      m.yaw = v[3]; m.headYaw = v[4]; m.pitch = v[5];
      m.limbSwing = v[6]; m.limbSwingAmount = v[7];
      m.swell = v[8];
      const e = r.last;
      m.hurtTime = e.hurtTime;
      m.deathTime = e.deathTime;
      m.dying = (e.flags & EF_DYING) !== 0;
      m.fireTicks = (e.flags & EF_FIRE) ? 20 : 0;
      m.aggressive = (e.flags & EF_AGGRO) !== 0;
      m.sheared = (e.flags & EF_SHEARED) !== 0;
      m.color = e.color;
      m.heldItem = e.heldItem;
      m.swingTicks = e.attackAnim > 0 ? Math.max(1, Math.round(e.attackAnim * 6)) : 0;
      if (m.dying && m.health > 0) m.health = 0;
    }
    this.d.entities.syncViews();
  }

  // ------------------------------------------------------- gameplay objects

  /** main.ts's dropped-item manager (entities/itemEntities.ts registry). */
  private items(): ItemEntityManager | null {
    const all = liveItemManagers();
    return all.length ? all[all.length - 1] : null;
  }

  /** Host: a client dropped a stack — it is ours now. */
  private spawnDrop(drop: { kind: number; id: number; item: number; count: number; dmg: number; x: number; y: number; z: number; ex?: number[] },
    _netId: number, _origin: number, vx: number, vy: number, vz: number): number | null {
    if (drop.kind !== DK_ITEM) return null;
    return this.items()?.spawn(stackWith(drop.item, drop.count, drop.dmg, drop.ex), drop.x, drop.y, drop.z, vx, vy, vz, 0)?.id ?? null;
  }

  /**
   * Host: a drop left the world. Items are found through the object net's own
   * id map, which only knows ids it assigned, so we look the net id up by
   * asking it through the same key it uses (an item it mirrors is the only
   * way we get told about one).
   */
  private removeDrop(kind: number, netId: number): boolean {
    if (kind !== DK_ITEM) return false;
    const items = this.items();
    if (!items) return false;
    for (const it of items.items) {
      if (it.collect > 0) continue;
      if (this.objs.netIdFor(it) === netId) { it.stack.count = 0; return true; }
    }
    return false;
  }

  /** Client: mirror an arrow from the host. */
  private spawnArrow(a: ArrowState): number | null {
    const speed = Math.hypot(a.vx, a.vy, a.vz);
    if (speed < 1e-4) return null;
    const arrow = this.d.entities.shootArrow(
      a.x, a.y, a.z, a.vx, a.vy, a.vz, speed, 1, (a.flags & AF_PLAYER) !== 0, 0, 0,
    );
    arrow.stuck = (a.flags & AF_STUCK) !== 0;
    return arrow.id;
  }

  /** Every block entity of `kind` within `r` blocks of (x, y, z). */
  private beNear(kind: number, x: number, y: number, z: number, r: number): Array<{ x: number; y: number; z: number; flat: number[] }> {
    const out: Array<{ x: number; y: number; z: number; flat: number[] }> = [];
    const r2 = r * r;
    if (kind === BE_CHEST) {
      for (const [key, pos] of this.chests()) {
        const dx = pos.x - x, dy = pos.y - y, dz = pos.z - z;
        if (dx * dx + dy * dy + dz * dz > r2) continue;
        const flat = this.chestFlat(key);
        if (flat) out.push({ ...pos, flat });
      }
      return out;
    }
    const f = liveFurnaces();
    const record = f?.serialize() ?? this.d.save.furnaces;
    for (const key of Object.keys(record)) {
      const [fx, fy, fz] = key.split(',').map(Number);
      const dx = fx - x, dy = fy - y, dz = fz - z;
      if (dx * dx + dy * dy + dz * dz > r2) continue;
      const flat = f?.peek(key)?.serialize() ?? record[key];
      if (flat && flat.length) out.push({ x: fx, y: fy, z: fz, flat });
    }
    return out;
  }

  private beWrite(kind: number, x: number, y: number, z: number, flat: number[]): void {
    const key = `${x},${y},${z}`;
    if (kind === BE_CHEST) {
      const stacks: Stack[] = [];
      for (let i = 0; i < CHEST_SLOTS; i++) {
        const id = flat[i * 3] | 0, count = flat[i * 3 + 1] | 0, dmg = flat[i * 3 + 2] | 0;
        stacks.push({ id, count, dmg });
      }
      deserializeExtras(stacks, flat, CHEST_SLOTS * 3);
      this.d.save.recordChest(key, stacks);
      return;
    }
    const f = liveFurnaces();
    if (f) f.load({ [key]: flat.slice() });
    else this.d.save.furnaces = { ...this.d.save.furnaces, [key]: flat.slice() };
    this.d.save.touch();
  }

  private beDrop(kind: number, x: number, y: number, z: number): void {
    const key = `${x},${y},${z}`;
    if (kind === BE_CHEST) this.d.save.forgetChest(key);
    else {
      const f = liveFurnaces();
      if (f) f.remove(key);
      else {
        const next = { ...this.d.save.furnaces };
        delete next[key];
        this.d.save.furnaces = next;
        this.d.save.touch();
      }
    }
  }

  private chests(): Map<string, { x: number; y: number; z: number }> {
    this.chestIndex.clear();
    for (const key of this.d.save.chests.keys()) {
      const [x, y, z] = key.split(',').map(Number);
      if (![x, y, z].every(Number.isFinite)) continue;
      this.chestIndex.set(key, { x, y, z });
    }
    return this.chestIndex;
  }

  private chestFlat(key: string): number[] | null {
    const stacks = this.d.save.chest(key);
    if (!stacks) return null;
    const flat: number[] = [];
    for (let i = 0; i < CHEST_SLOTS; i++) {
      const s = stacks[i];
      flat.push(s?.id ?? 0, s?.count ?? 0, s?.dmg ?? 0);
    }
    if (!flat.some((v) => v !== 0)) return null;
    serializeExtras(stacks.slice(0, CHEST_SLOTS), flat);
    return flat;
  }

  /** Client: a drop a remote player picked up is ours (main.ts's inventory). */
  private giveItem(item: number, count: number, dmg: number, ex?: number[]): void {
    const inv = liveInventory();
    if (!inv) return;
    const s = stackWith(item, count, dmg, ex);
    // a named / reworked stack never merges: Inventory.add only carries enchantments
    if (!s.name && !s.rep && !s.fw && !s.charged) { inv.add(item, count, dmg, s.ench); return; }
    const slot = inv.slots.find((x) => isEmpty(x));
    if (slot) { setStack(slot, s); inv.changed(); }
  }

  /** Client: the host says we were hurt — run our own armour / i-frame rules. */
  private applyRemoteHit(hit: NetHit): void {
    // through main.ts's player adapter, so a raised shield blocks a networked
    // hit the same way it blocks a mob's swing
    const me = this.d.entityHost.player;
    const src: DamageSource = { kind: hit.kind as DamageSource['kind'], x: hit.x, y: hit.y, z: hit.z, attackerId: hit.attacker };
    // a raised shield is the only thing that cancels a hit outright; the
    // i-frames (vardmg, mob grinds) still shove and flash, like vanilla
    if (me.damage(hit.amount, src) === 'blocked') return;
    this.d.player.hurt(hit.x - this.d.player.position.x, hit.z - this.d.player.position.z);
    me.knockback(hit.kbx, hit.kbz, 0.4);
    if (hit.fire) me.setOnFire?.(80);
  }

  /** Every player this peer simulates against (local one included). */
  private players(): Array<{ id: string; x: number; y: number; z: number }> {
    const out: Array<{ id: string; x: number; y: number; z: number }> = [{ id: '', x: 0, y: 0, z: 0 }];
    const p = this.d.player.position;
    out[0] = { id: this.session?.me.id ?? '', x: p.x, y: p.y, z: p.z };
    for (const t of this.d.entities.remotePlayers) out.push({ id: t.id, x: t.x, y: t.y, z: t.z });
    return out;
  }

  /**
   * Remote players as mob targets, rebuilt from the session's accepted states
   * (20 Hz). Damage is the host's job: it tells the client, which applies it
   * to its own player (health is client-authoritative in this protocol).
   *
   * A client keeps the same list, filled from the interpolated render poses:
   * its own swings are aimed at what it can see, and the host checks the
   * reach when the hit arrives.
   */
  private syncRemotePlayers(): void {
    const s = this.session;
    if (!s) {
      this.d.entities.remotePlayers = [];
      this.remoteTargets.clear();
      return;
    }
    if (this.replica) {
      const seen = new Set<string>();
      for (const v of s.remotePlayers(this.peerViews)) {
        seen.add(v.id);
        const t = this.targetFor(v.id, s);
        t.x = v.v[0]; t.y = v.v[1]; t.z = v.v[2];
        t.alive = (v.state.flags & PF_DEAD) === 0;
        t.armor = v.state.armor;
      }
      this.publishTargets(seen);
      return;
    }
    if (!s.isHost) {
      this.d.entities.remotePlayers = [];
      this.remoteTargets.clear();
      return;
    }
    const seen = new Set<string>();
    for (const peer of s.peers.values()) {
      const st = peer.accepted;
      if (!st || !s.members.has(peer.id)) continue;
      seen.add(peer.id);
      const t = this.targetFor(peer.id, s);
      t.x = st.x; t.y = st.y; t.z = st.z;
      t.alive = (st.flags & PF_DEAD) === 0;
      t.armor = st.armor;
    }
    this.publishTargets(seen);
  }

  /** One target per peer id, reused across ticks (never rebuilt per frame). */
  private targetFor(id: string, s: NetSession): RemotePlayer {
    let t = this.remoteTargets.get(id);
    if (!t) {
      // on a client the local swing is what sends the hit, so a stray call
      // here (an arrow, say) is forwarded to the host as well
      t = new RemotePlayer(id, (hit) => (s.isHost
        ? this.objs.hurtPeer(id, hit)
        : s.hitPlayer(id, hit.amount, hit.x, hit.z, 0.4)));
      this.remoteTargets.set(id, t);
    }
    return t;
  }

  private publishTargets(seen: Set<string>): void {
    for (const id of [...this.remoteTargets.keys()]) if (!seen.has(id)) this.remoteTargets.delete(id);
    this.outTargets.length = 0;
    for (const t of this.remoteTargets.values()) this.outTargets.push(t);
    this.d.entities.remotePlayers = this.outTargets;
  }

  /** Called every game tick from the session timer when hosting (deferred edits). */
  tick(): void {
    this.flushDeferred();
    this.session?.expireStaleHurt(1000);
    this.syncRemotePlayers();
    this.objs.tick();
  }
}

/** A remote player as the mobs see them: position from the wire, damage sent
 *  back to the client that owns the body (health travels back in PSTATE). */
class RemotePlayer implements PlayerTarget {
  x = 0; y = 0; z = 0;
  readonly eyeY = 1.62;
  readonly width = 0.6;
  readonly height = 1.8;
  alive = true;
  creative = false;
  spectator = false;
  armor: readonly number[] = [];
  private burning = false;
  constructor(readonly id: string, private readonly onHit: (hit: NetHit) => void) {}
  damage(amount: number, src: DamageSource): void {
    if (amount <= 0) return;
    const fire = this.burning;
    this.burning = false;
    this.onHit({
      amount, kind: src.kind, x: src.x ?? this.x, y: src.y ?? this.y, z: src.z ?? this.z,
      attacker: src.attackerId ?? 0, kbx: this.x - (src.x ?? this.x), kbz: this.z - (src.z ?? this.z), fire,
    });
  }
  knockback(): void { /* the client shoves its own player when the hit lands */ }
  setOnFire(): void { this.burning = true }
}
